/**
 * ReactFlow wrapper for the trace graph.
 *
 * Takes the API ``{nodes, edges}`` plus the anchor id, runs the
 * layout pass, then renders one custom node per kind with edges
 * coloured by edge-kind so the operator can read the dependency
 * direction at a glance (received/triggered are inbound, emitted is
 * outbound, contains/chained are intra-execution).
 *
 * Selection is lifted to the parent — the sidebar shows the
 * currently-selected node's details and the "Open in entity" link.
 */
import * as React from "react";
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  MarkerType,
  Position,
  ReactFlowProvider,
  type Edge,
  type Node,
} from "reactflow";
import "reactflow/dist/style.css";

import type {
  TraceEdge,
  TraceEdgeKind,
  TraceNode,
} from "@/lib/api-trace";

import { layoutTraceGraphWithGroups } from "./layout";
import {
  TRACE_NODE_HEIGHT,
  TRACE_NODE_TYPES,
  TRACE_NODE_WIDTH,
  type TraceGroupCardData,
  type TraceNodeCardData,
} from "./nodeTypes";


// Edge colours: matches the semantic of each edge kind. Receiving an
// event (received) is success-tinted; triggered/chained are accent;
// contains is dim (it's a "parent/child" structural relationship);
// emitted is warning-tinted to flag the side-effect.
const EDGE_COLOR: Record<TraceEdgeKind, string> = {
  received: "hsl(var(--success))",
  triggered: "hsl(var(--accent))",
  contains: "hsl(var(--muted-foreground))",
  emitted: "hsl(var(--warning))",
  chained: "hsl(var(--info))",
};


type AnyTraceNode =
  | Node<TraceNodeCardData>
  | Node<TraceGroupCardData>;

interface BuildResult {
  rfNodes: AnyTraceNode[];
  hiddenEdgeKeys: Set<string>;
  collapsedChildToGroup: Map<string, string>;
}

function buildRfNodes(
  nodes: TraceNode[],
  edges: TraceEdge[],
  anchorId: string,
  selectedId: string | null,
  expanded: Set<string>,
  collapsed: Set<string>,
  onToggle: (groupId: string, wasCollapsed: boolean) => void,
): BuildResult {
  const { nodes: laidOut, groups, hiddenEdgeKeys, collapsedChildToGroup } =
    layoutTraceGraphWithGroups(nodes, edges, anchorId, {
      expandedGroupIds: expanded,
      collapsedGroupIds: collapsed,
    });

  // Groups must render BEFORE their children so ReactFlow picks up
  // the parentNode reference (z-order + parent-lookup are both
  // resolved in array order).
  const out: AnyTraceNode[] = [];
  for (const g of groups) {
    out.push({
      id: g.id,
      type: "traceGroup",
      position: g.position,
      data: {
        label: g.label,
        badge: g.badge,
        variant: g.variant,
        status: g.status,
        collapsed: g.collapsed,
        onToggle: () => onToggle(g.id, g.collapsed),
      },
      draggable: false,
      connectable: false,
      // The group node IS interactive (chevron click), but we don't
      // route ReactFlow selection through it — it's not an "entity".
      selectable: false,
      // Source/target positions so re-routed edges (when collapsed)
      // attach to the group's left/right edges cleanly.
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      style: {
        width: g.width,
        height: g.height,
        background: "transparent",
        border: "none",
        padding: 0,
        // Render BEHIND children — ReactFlow stacks by document order,
        // but a low z-index keeps edges from disappearing under us.
        zIndex: -1,
      },
    });
  }
  for (const n of laidOut) {
    out.push({
      id: n.id,
      type: "trace",
      position: n.position,
      // ``parentNode`` links the child to its group box. Children with
      // a parent use position COORDINATES relative to the parent.
      ...(n.parentNode ? { parentNode: n.parentNode, extent: "parent" as const } : {}),
      data: {
        kind: n.kind,
        label: n.label,
        status: (n.data as { status?: string }).status,
        isAnchor: n.id === anchorId,
        isSelected: n.id === selectedId,
      },
      draggable: false,
      connectable: false,
      selectable: true,
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      style: {
        width: TRACE_NODE_WIDTH,
        height: TRACE_NODE_HEIGHT,
        background: "transparent",
        border: "none",
        padding: 0,
      },
    });
  }
  return { rfNodes: out, hiddenEdgeKeys, collapsedChildToGroup };
}


function buildRfEdges(
  edges: TraceEdge[],
  hidden: Set<string>,
  rewrites: Map<string, string>,
): Edge[] {
  // Track deduped edge keys after rewrite — multiple originals can
  // collapse onto the same group→group edge once the children are
  // hidden, and we'd otherwise render 7 parallel arrows from the
  // same box to the same neighbour.
  const seen = new Set<string>();
  return edges.flatMap((e, i) => {
    // Hide edges that are now visually implicit via group containment
    // (e.g. "contains" edges from an execution to its node-execs
    // inside the same execution box).
    if (hidden.has(`${e.source}->${e.target}:${e.kind}`)) return [];
    const source = rewrites.get(e.source) ?? e.source;
    const target = rewrites.get(e.target) ?? e.target;
    // After rewrite, an edge that was entirely INSIDE a now-collapsed
    // group (e.g. contains, or chained internal) would become a
    // self-loop on the group node — drop those.
    if (source === target) return [];
    const dedupeKey = `${source}->${target}:${e.kind}`;
    if (seen.has(dedupeKey)) return [];
    seen.add(dedupeKey);
    const color = EDGE_COLOR[e.kind] ?? "hsl(var(--muted-foreground))";
    return [{
      // The backend doesn't currently send edge ids — synthesise a
      // stable one from rewritten source+target+kind+index so
      // ReactFlow's diffing doesn't churn between renders AND
      // collapsed-group rewrites get a fresh id.
      id: `${source}->${target}:${e.kind}:${i}`,
      source,
      target,
      type: "default",
      label: e.kind,
      labelStyle: {
        fontSize: 10,
        fontWeight: 500,
        fill: "hsl(var(--fg))",
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
      },
      labelBgStyle: {
        fill: "hsl(var(--elevated))",
        fillOpacity: 0.95,
        stroke: color,
        strokeOpacity: 0.3,
        strokeWidth: 1,
      },
      labelBgPadding: [4, 2],
      labelBgBorderRadius: 4,
      style: { stroke: color, strokeWidth: 1.5 },
      markerEnd: { type: MarkerType.ArrowClosed, color, width: 14, height: 14 },
    }];
  });
}


export interface TraceCanvasProps {
  nodes: TraceNode[];
  edges: TraceEdge[];
  anchorId: string;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

export function TraceCanvas({
  nodes,
  edges,
  anchorId,
  selectedId,
  onSelect,
}: TraceCanvasProps) {
  // Per-group user overrides on the auto-collapse heuristic. The Sets
  // reset when a new trace loads (the `anchorId` change triggers
  // useEffect via key invalidation downstream); inside one trace the
  // user's expand/collapse choices stick.
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = React.useState<Set<string>>(new Set());
  React.useEffect(() => {
    // Fresh trace → wipe overrides so the heuristic reapplies.
    setExpanded(new Set());
    setCollapsed(new Set());
  }, [anchorId]);

  // The card knows whether it is currently rendered as collapsed (the
  // layout computed it from heuristic + overrides). When the user
  // clicks the header it sends BOTH the id and the current state, so
  // we just flip it by writing to the opposite override set.
  const handleToggle = React.useCallback(
    (groupId: string, wasCollapsed: boolean) => {
      setExpanded((prev) => {
        const next = new Set(prev);
        if (wasCollapsed) next.add(groupId);
        else next.delete(groupId);
        return next;
      });
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (wasCollapsed) next.delete(groupId);
        else next.add(groupId);
        return next;
      });
    },
    [],
  );

  const { rfNodes, hiddenEdgeKeys, collapsedChildToGroup } = React.useMemo(
    () => buildRfNodes(nodes, edges, anchorId, selectedId, expanded, collapsed, handleToggle),
    [nodes, edges, anchorId, selectedId, expanded, collapsed, handleToggle],
  );
  const rfEdges = React.useMemo(
    () => buildRfEdges(edges, hiddenEdgeKeys, collapsedChildToGroup),
    [edges, hiddenEdgeKeys, collapsedChildToGroup],
  );

  return (
    <ReactFlowProvider>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={TRACE_NODE_TYPES}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        panOnScroll
        zoomOnScroll={false}
        minZoom={0.3}
        maxZoom={1.5}
        onNodeClick={(_, node) => onSelect(node.id)}
        onPaneClick={() => onSelect(null)}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </ReactFlowProvider>
  );
}
